/**
 * Formular Helper - Utility-Funktionen für Formularverwaltung
 */

export const formHelper = {
  /**
   * Validiert ein Feld basierend auf seinen Validierungsregeln
   * @param {Object} field - Das zu validierende Feld
   * @param {any} value - Der Wert, der validiert werden soll
   * @returns {Object} - { valid: boolean, error: string | null }
   */
  validateField(field, value) {
    if (field.required && (!value || value.toString().trim() === "")) {
      return {
        valid: false,
        error: `${field.label} ist erforderlich`
      };
    }

    if (!value) {
      return { valid: true, error: null };
    }

    switch (field.validation) {
      case "email":
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return {
            valid: false,
            error: "Gültige E-Mail-Adresse erforderlich"
          };
        }
        break;

      case "url":
        if (!/^https?:\/\/.+/.test(value)) {
          return {
            valid: false,
            error: "Gültige URL erforderlich"
          };
        }
        break;

      case "number":
        if (isNaN(value)) {
          return {
            valid: false,
            error: "Nur Zahlen erlaubt"
          };
        }
        break;

      case "phone":
        if (!/^[\d\s\-+()]+$/.test(value)) {
          return {
            valid: false,
            error: "Gültige Telefonnummer erforderlich"
          };
        }
        break;

      case "pattern":
        if (field.pattern && !new RegExp(field.pattern).test(value)) {
          return {
            valid: false,
            error: "Format ungültig"
          };
        }
        break;

      case "minLength":
        if (field.minLength && value.toString().length < field.minLength) {
          return {
            valid: false,
            error: `Mindestens ${field.minLength} Zeichen erforderlich`
          };
        }
        break;

      case "maxLength":
        if (field.maxLength && value.toString().length > field.maxLength) {
          return {
            valid: false,
            error: `Maximal ${field.maxLength} Zeichen erlaubt`
          };
        }
        break;
    }

    return { valid: true, error: null };
  },

  /**
   * Exportiert ein Formular als JSON
   * @param {Object} form - Das Formular
   * @returns {string} - JSON-String
   */
  exportFormAsJson(form) {
    return JSON.stringify(form, null, 2);
  },

  /**
   * Importiert ein Formular aus JSON
   * @param {string} jsonString - JSON-String
   * @returns {Object|null} - Das importierte Formular oder null
   */
  importFormFromJson(jsonString) {
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      console.error("Fehler beim Importieren des Formulars:", e);
      return null;
    }
  },

  /**
   * Erstellt einen CSV-Export aus Formulardaten
   * @param {Array} submissions - Array von Formulareinreichungen
   * @returns {string} - CSV-String
   */
  exportSubmissionsAsCsv(submissions) {
    if (!submissions || submissions.length === 0) {
      return "";
    }

    const headers = Object.keys(submissions[0]);
    return [
      headers.join(","),
      ...submissions.map(row =>
        headers
          .map(header => {
            const value = row[header];
            return typeof value === "string" && value.includes(",")
              ? `"${value}"`
              : value;
          })
          .join(",")
      )
    ].join("\n");
  },

  /**
   * Generiert einen zufälligen Feldnamen
   * @param {string} fieldType - Der Feldtyp
   * @returns {string} - Ein eindeutiger Feldname
   */
  generateFieldName(fieldType) {
    const timestamp = Date.now().toString(36);
    return `${fieldType}_${timestamp}`;
  },

  /**
   * Klont ein Formular
   * @param {Object} form - Das zu klonende Formular
   * @returns {Object} - Ein Klon des Formulars
   */
  cloneForm(form) {
    const clonedForm = JSON.parse(JSON.stringify(form));
    clonedForm.id = this.generateFieldName("form");
    clonedForm.name = `${form.name} (Kopie)`;
    clonedForm.createdAt = new Date();
    clonedForm.updatedAt = new Date();
    return clonedForm;
  },

  /**
   * Sortiert Formulare nach Erstellungsdatum
   * @param {Array} forms - Array von Formularen
   * @param {string} order - 'asc' oder 'desc'
   * @returns {Array} - Sortierte Formulare
   */
  sortFormsByDate(forms, order = "desc") {
    return [...forms].sort((a, b) => {
      const aDate = new Date(a.createdAt);
      const bDate = new Date(b.createdAt);
      return order === "desc" ? bDate - aDate : aDate - bDate;
    });
  }
};

export default formHelper;
